/**
 * LiveStreamingChatMessageItem — one row of the live-streaming chat overlay:
 * a tappable avatar, a tappable username, and the message text below it.
 *
 * Flutter source of truth: `CommunityLiveMessageItem`
 * (`community_live_message_item.dart:11-85`) — a `Row` with:
 *  - a `GestureDetector` around a `CircleBorderAvatar`
 *    (`TextUtils.generateUserProfileImageUrl(userId)`) that navigates to that
 *    user's details (`:34-43`), and
 *  - an `Expanded` `Column` with a `RichText` username (`TapGestureRecognizer`
 *    → navigate to user details, `:57-67`) above the plain message `Text`
 *    (`message.text`, a no-op `GestureDetector`, `:71-77`).
 *
 * Unlike the community-wall author row (`CommunityMessageAuthorInfo`), the live
 * item NEVER substitutes a `"You"` label — it always renders the raw
 * `messageUsersInfo.userName` (Flutter `:59`). There is no timestamp, reply
 * preview, like button, or media here.
 *
 * Both the avatar and the username tap through to the SAME
 * `Navigator.navigateToUserDetails(userId)` the wall author row uses
 * (`CommunityMessageAuthorInfo.tsx:74`); the message text is intentionally
 * non-interactive (Flutter's no-op `GestureDetector`, `:72`).
 *
 * Theme / responsive tokens (UI parity, web deviation permitted):
 *  - Avatar size → `responsive.avatar.small` (= 32; Flutter
 *    `communityLiveMessageProfileSize = 28` — closest token).
 *  - Username color/size → `theme.text.secondary` / `responsive.font.base`
 *    (Flutter `commentUserStyle`: `disabledTextColor` #C0C0C0, 14 —
 *    `app_styles.dart:1465-1471`).
 *  - Message color/size → `theme.text.primary` / `responsive.font.base`
 *    (Flutter `commentStyle`: `commentColor` #F8F8FF, 14 —
 *    `app_styles.dart:1376-1383`).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { useEnterOrSpaceActivation } from '@/presentation/hooks/useEnterOrSpaceActivation';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';

export interface LiveStreamingChatMessageItemProps {
  /** The live chat message to render (consumed directly; no remapper). */
  message: CommunityChatMessageEntity;
}

export const LiveStreamingChatMessageItem: React.FC<LiveStreamingChatMessageItemProps> = ({
  message,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const userId = message.messageUsersInfo.userId;

  const handleTap = (): void => {
    Navigator.navigateToUserDetails(userId);
  };
  const handleKeyDown = useEnterOrSpaceActivation<HTMLElement>(handleTap);

  const avatarSize = responsive.avatar.small;

  return (
    <div
      {...qaAttr({ id: `live-chat-message-${message.id}` })}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: responsive.spacing.sm,
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={handleTap}
        onKeyDown={handleKeyDown}
        style={{ cursor: 'pointer', flexShrink: 0, lineHeight: 0 }}
      >
        <CachedImage
          src={UserUtils.generateUserProfileImageUrl(userId)}
          alt=""
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: responsive.borderRadius.round,
            objectFit: 'cover',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          flex: 1,
          gap: responsive.spacing.xxs,
        }}
      >
        <span
          role="button"
          tabIndex={0}
          onClick={handleTap}
          onKeyDown={handleKeyDown}
          style={{
            color: theme.text.secondary,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.regular,
            cursor: 'pointer',
            alignSelf: 'flex-start',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {message.messageUsersInfo.userName}
        </span>
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.regular,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.text}
        </span>
      </div>
    </div>
  );
};
